import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Logo from "./Logo";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/works", label: "Works" },
  { to: "/publications", label: "Publications" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Over-hero state: navbar transparent with white content (for top of a hero image).
  // Off-hero: solid white with black content.
  // We detect hero by pathname (routes with a full-bleed hero) + scroll position.
  const hasHero =
    pathname === "/" ||
    (pathname.startsWith("/works/") && pathname !== "/works");
  const overHero = hasHero && !scrolled && !open;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-smooth ${
          overHero
            ? "bg-transparent border-b border-transparent"
            : "bg-paper border-b border-mist"
        }`}
      >
        <div className="container-page flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className={`flex items-center gap-3 transition-colors ${
              overHero ? "text-paper" : "text-ink"
            }`}
            aria-label="RM.NM Architects — Home"
          >
            <Logo size={9} />
            <div className="leading-tight">
              <div className="font-body font-semibold text-lg tracking-widest2">
                RM.NM
              </div>
              <div
                className={`text-[9px] tracking-widest3 uppercase -mt-0.5 ${
                  overHero ? "text-paper/80" : "text-smoke"
                }`}
              >
                Architects
              </div>
            </div>
          </Link>

          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            aria-expanded={open}
            className="w-10 h-10 flex flex-col items-center justify-center gap-1.5"
          >
            <span
              className={`block w-7 h-px transition-colors ${
                overHero ? "bg-paper" : "bg-ink"
              }`}
            />
            <span
              className={`block w-7 h-px transition-colors ${
                overHero ? "bg-paper" : "bg-ink"
              }`}
            />
            <span
              className={`block w-7 h-px transition-colors ${
                overHero ? "bg-paper" : "bg-ink"
              }`}
            />
          </button>
        </div>
      </header>

      {/* Right-side drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[60] bg-ink/40 backdrop-blur-sm"
              aria-hidden
            />
            <motion.aside
              key="drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.5, ease: [0.33, 1, 0.68, 1] }}
              className="fixed top-0 right-0 bottom-0 z-[70] w-[85vw] max-w-[460px] bg-paper border-l border-mist flex flex-col"
              role="dialog"
              aria-modal="true"
              aria-label="Navigation"
            >
              <div className="flex items-center justify-between h-16 md:h-20 px-6 md:px-10 border-b border-mist">
                <div className="text-ink">
                  <Logo size={9} />
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  className="w-10 h-10 relative flex items-center justify-center"
                >
                  <span className="absolute w-6 h-px bg-ink rotate-45" />
                  <span className="absolute w-6 h-px bg-ink -rotate-45" />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto px-6 md:px-10 py-10 md:py-12">
                <ol className="space-y-5 md:space-y-6">
                  {NAV.map((item, i) => (
                    <motion.li
                      key={item.to}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.5,
                        delay: 0.15 + i * 0.06,
                        ease: [0.33, 1, 0.68, 1],
                      }}
                    >
                      <NavLink
                        to={item.to}
                        end={item.end}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          `group flex items-baseline gap-4 transition-colors ${
                            isActive ? "text-ink" : "text-smoke hover:text-ink"
                          }`
                        }
                      >
                        <span className="font-display text-[10px] tracking-widest3 w-6 text-smoke">
                          0{i + 1}
                        </span>
                        <span className="font-display text-3xl md:text-5xl tracking-wider leading-none">
                          {item.label}
                        </span>
                      </NavLink>
                    </motion.li>
                  ))}
                </ol>
              </nav>

              <div className="px-6 md:px-10 py-8 border-t border-mist text-[11px] tracking-widest3 uppercase text-smoke space-y-4">
                <div className="space-y-1">
                  <a
                    href="mailto:contact@rcmnrm.co.in"
                    className="block text-ink hover:opacity-60 transition-opacity"
                  >
                    contact@rcmnrm.co.in
                  </a>
                  <a
                    href="tel:+919426209548"
                    className="block text-ink hover:opacity-60 transition-opacity"
                  >
                    +91 94262 09548
                  </a>
                </div>
                <div className="flex gap-5">
                  <a
                    href="https://www.instagram.com/rm.nm.architects/"
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-ink transition-colors"
                  >
                    Instagram
                  </a>
                  <a
                    href="https://www.facebook.com/rm.nm.architects"
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-ink transition-colors"
                  >
                    Facebook
                  </a>
                </div>
                <div className="pt-2 opacity-70">
                  Bhavnagar — Gujarat — Since 1976
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
